import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch, apiDownload } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatNumber } from '../../../utils/formatters';
import { formatDataRoma } from '../../../utils/dateRoma';
import { urlPubblica } from '../../../pubblico/apiPubblica';
import { API_DOCS } from './ModelliPaginaPage.api';
import {
  SEGNAPOSTO_TESTO, REGOLE_PRENOTAZIONE, REGOLE_CONDIZIONALE, REGOLE_TESTO, REGOLE_IMMAGINI, REGOLE_COLORI,
  REGOLE_HTML, REGOLE_CSS, ISTRUZIONI_INIZIALI, COME_SI_USA, generaMarkdownGuida,
} from './guidaSegnaposto';

interface ModelloElenco {
  id: number; nome: string; descrizione: string | null; updatedAt: string; numeroCantieri: number; risorse: string[];
}
interface RisorsaModello { nomeFile: string; url: string; dimensione: number }
interface DettaglioModello {
  id: number; societaId: number; nome: string; descrizione: string | null; html: string; css: string; risorse: RisorsaModello[];
}
// apiFetch allega a ogni errore il corpo della risposta: il 400 sui modelli ha l'elenco `errori`.
type ErroreApi = Error & { status?: number; payload?: { errori?: unknown } };

const MOD = 'commerciale.percorso_configurazione';
const FORMATI_IMMAGINE = 'image/jpeg,image/png,image/webp,image/svg+xml,.jpg,.jpeg,.png,.webp,.svg';

// Nome adatto a un file scaricato: niente spazi o caratteri strani.
const nomeFileSicuro = (nome: string): string => nome.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'modello';

const erroriDa = (e: unknown): string[] => {
  const errori = (e as ErroreApi).payload?.errori;
  return Array.isArray(errori) ? errori.filter((x): x is string => typeof x === 'string') : [];
};

const IconaAnteprima = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

function scaricaTesto(nomeFile: string, contenuto: string, tipo: string) {
  const url = URL.createObjectURL(new Blob([contenuto], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ModaleModello({ societaId, modello, onClose, onSalvato }: {
  societaId: string; modello: ModelloElenco | null; onClose: () => void; onSalvato: () => void;
}) {
  const [nome, setNome] = useState(modello?.nome ?? '');
  const [descrizione, setDescrizione] = useState(modello?.descrizione ?? '');
  const [fileHtml, setFileHtml] = useState<File | null>(null);
  const [fileCss, setFileCss] = useState<File | null>(null);
  const [immagini, setImmagini] = useState<File[]>([]);
  const [rimosse, setRimosse] = useState<string[]>([]);
  const [dettaglio, setDettaglio] = useState<DettaglioModello | null>(null);
  const [errore, setErrore] = useState('');
  const [errori, setErrori] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  const inputImmagini = useRef<HTMLInputElement>(null);
  // Valori con cui si apre la modale: servono a sapere se ci sono modifiche non salvate.
  const [iniziali] = useState(() => ({ nome: modello?.nome ?? '', descrizione: modello?.descrizione ?? '' }));

  const isDirty = nome !== iniziali.nome || descrizione !== iniziali.descrizione
    || fileHtml !== null || fileCss !== null || immagini.length > 0 || rimosse.length > 0;
  useDirtyForm(isDirty, modello ? `Modello pagina ${modello.nome}` : 'Nuovo modello pagina');

  const idModello = modello?.id;
  useEffect(() => {
    if (idModello === undefined) return;
    // La modale si può chiudere prima della risposta: allora non si aggiorna più nulla.
    let attiva = true;
    // @api GET /api/percorso-acquirenti/modelli/:id -> {"success":true,"data":{"id":"number","societaId":"number","nome":"string","descrizione":"string","html":"string","css":"string","risorse":"array"}}
    apiFetch<{ success: boolean; data: DettaglioModello }>(`/api/percorso-acquirenti/modelli/${idModello}`)
      .then(r => { if (attiva) setDettaglio(r.data); })
      .catch(e => { if (attiva) setErrore((e as Error).message); });
    return () => { attiva = false; };
  }, [idModello]);

  // Le immagini scelte si sommano a quelle già scelte; con lo stesso nome vale l'ultima.
  const aggiungiImmagini = (files: FileList | null) => {
    if (files) {
      const nuove = Array.from(files);
      setImmagini(cur => {
        const perNome = new Map<string, File>();
        cur.concat(nuove).forEach(f => perNome.set(f.name.toLowerCase(), f));
        return Array.from(perNome.values());
      });
    }
    if (inputImmagini.current) inputImmagini.current.value = '';
  };

  const alternaRimozione = (nomeFile: string) =>
    setRimosse(cur => (cur.includes(nomeFile) ? cur.filter(n => n !== nomeFile) : [...cur, nomeFile]));

  const salva = async () => {
    setErrore('');
    setErrori([]);
    if (!nome.trim()) {
      setErrore('Il nome è obbligatorio');
      return;
    }
    if (!modello && (!fileHtml || !fileCss)) {
      setErrore('Scegli il file HTML e il file CSS del modello');
      return;
    }
    const fd = new FormData();
    if (!modello) fd.append('societaId', societaId);
    fd.append('nome', nome.trim());
    fd.append('descrizione', descrizione.trim());
    if (fileHtml) fd.append('html', fileHtml);
    if (fileCss) fd.append('css', fileCss);
    immagini.forEach(f => fd.append('immagini', f));
    if (rimosse.length > 0) fd.append('rimuoviImmagini', JSON.stringify(rimosse));
    setSalvando(true);
    try {
      if (modello) {
        // @api PUT /api/percorso-acquirenti/modelli/:id [FormData nome, descrizione, html, css, immagini, rimuoviImmagini] -> {"success":true,"data":{"id":"number"}}
        await apiFetch(`/api/percorso-acquirenti/modelli/${modello.id}`, { method: 'PUT', body: fd });
      } else {
        // @api POST /api/percorso-acquirenti/modelli [FormData societaId, nome, descrizione, html, css, immagini] -> {"success":true,"data":{"id":"number"}}
        await apiFetch('/api/percorso-acquirenti/modelli', { method: 'POST', body: fd });
      }
      showToast(modello ? `Modello ${nome.trim()} aggiornato` : `Modello ${nome.trim()} creato`);
      onSalvato();
    } catch (e) {
      // Un modello non valido non si salva: il backend elenca tutti gli errori trovati nei file.
      setErrore((e as Error).message);
      setErrori(erroriDa(e));
    } finally {
      setSalvando(false);
    }
  };

  const esistenti = dettaglio?.risorse ?? [];
  return (
    // Niente chiusura al clic sullo sfondo: un clic fuori posto farebbe perdere file e testi scelti.
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--md" role="dialog" aria-modal="true" aria-label={modello ? 'Modifica modello' : 'Nuovo modello'}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{modello ? 'Modifica modello' : 'Nuovo modello'}</h2>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-percorso__sezione">
          {errore && (
            <div className="bop-alert bop-alert--danger" role="alert">
              <div>{errore}</div>
              {errori.length > 0 && (
                <ul className="bop-percorso__elenco">
                  {errori.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="mod-nome">Nome</label>
            <input id="mod-nome" className="bop-input" value={nome} onChange={e => setNome(e.target.value)} maxLength={100} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="mod-descrizione">Descrizione (facoltativa)</label>
            <textarea id="mod-descrizione" className="bop-input bop-percorso__textarea" value={descrizione} onChange={e => setDescrizione(e.target.value)} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="mod-html">{modello ? 'Sostituisci il file HTML (facoltativo)' : 'File HTML'}</label>
            <input id="mod-html" className="bop-input" type="file" accept=".html,.htm,text/html" onChange={e => setFileHtml(e.target.files?.[0] ?? null)} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="mod-css">{modello ? 'Sostituisci il file CSS (facoltativo)' : 'File CSS'}</label>
            <input id="mod-css" className="bop-input" type="file" accept=".css,text/css" onChange={e => setFileCss(e.target.files?.[0] ?? null)} />
          </div>

          <div className="bop-form-group">
            <label className="bop-label" htmlFor="mod-immagini">{modello ? 'Aggiungi o sostituisci immagini' : 'Immagini (facoltative)'}</label>
            <input id="mod-immagini" ref={inputImmagini} className="bop-input" type="file" multiple accept={FORMATI_IMMAGINE} onChange={e => aggiungiImmagini(e.target.files)} />
            <span className="bop-text-muted">jpg, png, webp o svg, fino a 2 MB ciascuna e 30 per modello. Nel modello si citano come immagini/nomefile.</span>
          </div>
          {immagini.map(f => (
            <div className="bop-percorso__riga" key={f.name}>
              <span>{f.name}</span>
              <span className="bop-text-muted">{formatNumber(f.size / 1024)} KB</span>
              <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setImmagini(cur => cur.filter(x => x !== f))} aria-label={`Togli ${f.name}`}>Togli</button>
            </div>
          ))}

          {modello && (
            <div className="bop-form-group">
              <span className="bop-label">Immagini del modello</span>
              {!dettaglio && !errore && <span className="bop-text-muted">Caricamento…</span>}
              {dettaglio && esistenti.length === 0 && <span className="bop-text-muted">Nessuna immagine caricata.</span>}
              {esistenti.map(r => {
                const rimossa = rimosse.includes(r.nomeFile);
                return (
                  <div className={`bop-percorso__immagine${rimossa ? ' bop-percorso__immagine--rimossa' : ''}`} key={r.nomeFile}>
                    <img className="bop-percorso__miniatura" src={/^https?:/.test(r.url) ? r.url : urlPubblica(r.url)} alt="" />
                    <span>{r.nomeFile}</span>
                    <span className="bop-text-muted">{formatNumber(r.dimensione / 1024)} KB</span>
                    <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => alternaRimozione(r.nomeFile)} aria-label={`${rimossa ? 'Mantieni' : 'Rimuovi'} ${r.nomeFile}`}>
                      {rimossa ? 'Mantieni' : 'Rimuovi'}
                    </button>
                  </div>
                );
              })}
              {rimosse.length > 0 && <span className="bop-text-muted">Le immagini rimosse non devono più essere citate nel modello, altrimenti il salvataggio viene rifiutato.</span>}
            </div>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>{salvando ? 'Salvataggio…' : 'Salva'}</button>
        </div>
      </div>
    </div>
  );
}

function ModaleElimina({ modello, onClose, onEliminato }: { modello: ModelloElenco; onClose: () => void; onEliminato: () => void }) {
  const [errore, setErrore] = useState('');
  const [eliminando, setEliminando] = useState(false);

  const elimina = async () => {
    setEliminando(true);
    setErrore('');
    try {
      // @api DELETE /api/percorso-acquirenti/modelli/:id -> {"success":true}
      await apiFetch(`/api/percorso-acquirenti/modelli/${modello.id}`, { method: 'DELETE' });
      showToast(`Modello ${modello.nome} eliminato`);
      onEliminato();
    } catch (e) {
      // 409: il modello è usato da dei cantieri; il messaggio del server dice quanti.
      setErrore((e as Error).message);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--sm" role="dialog" aria-modal="true" aria-label="Elimina modello">
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Elimina modello</h2>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-percorso__sezione">
          <p>Vuoi eliminare il modello <strong>{modello.nome}</strong>? I suoi file e le sue immagini vengono cancellati e l'operazione non si può annullare.</p>
          {errore && <div className="bop-alert bop-alert--danger" role="alert">{errore}</div>}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-danger" onClick={elimina} disabled={eliminando}>{eliminando ? 'Eliminazione…' : 'Elimina'}</button>
        </div>
      </div>
    </div>
  );
}

function ElencoRegole({ regole }: { regole: string[] }) {
  return <ul className="bop-percorso__elenco">{regole.map((r, i) => <li key={i}>{r}</li>)}</ul>;
}

function ModaleGuida({ onClose }: { onClose: () => void }) {
  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--lg" role="dialog" aria-modal="true" aria-label="Guida ai segnaposto">
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Guida ai segnaposto</h2>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-percorso__guida">
          <p className="bop-text-muted">
            Da consegnare a Claude (o a un grafico) insieme al modello di esempio per far preparare un nuovo modello di pagina.
            Scaricala come file .md per allegarla alla richiesta.
          </p>
          <h3 className="bop-card__title">Istruzioni</h3>
          <ElencoRegole regole={ISTRUZIONI_INIZIALI} />

          <h3 className="bop-card__title">Segnaposto di testo</h3>
          <table className="bop-table">
            <thead><tr><th>Segnaposto</th><th>Contenuto</th></tr></thead>
            <tbody>
              {SEGNAPOSTO_TESTO.map(v => (
                <tr key={v.codice}><td><span className="bop-percorso__codice">{v.codice}</span></td><td>{v.significato}</td></tr>
              ))}
            </tbody>
          </table>

          <h3 className="bop-card__title">Il blocco di prenotazione</h3>
          <ElencoRegole regole={REGOLE_PRENOTAZIONE} />
          <h3 className="bop-card__title">Contenuti facoltativi</h3>
          <ElencoRegole regole={REGOLE_CONDIZIONALE} />
          <h3 className="bop-card__title">Regole sui segnaposto</h3>
          <ElencoRegole regole={REGOLE_TESTO} />
          <h3 className="bop-card__title">Immagini</h3>
          <ElencoRegole regole={REGOLE_IMMAGINI} />
          <h3 className="bop-card__title">Colori del cantiere</h3>
          <ElencoRegole regole={REGOLE_COLORI} />
          <h3 className="bop-card__title">Regole del file HTML</h3>
          <ElencoRegole regole={REGOLE_HTML} />
          <h3 className="bop-card__title">Regole del file CSS</h3>
          <ElencoRegole regole={REGOLE_CSS} />
          <h3 className="bop-card__title">Come si usa</h3>
          <ol className="bop-percorso__elenco">{COME_SI_USA.map((r, i) => <li key={i}>{r}</li>)}</ol>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={() => scaricaTesto('guida-segnaposto-modelli-pagina.md', generaMarkdownGuida(), 'text/markdown;charset=utf-8')}>Scarica come .md</button>
          <button className="bop-btn bop-btn-primary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

export default function ModelliPaginaPage() {
  const navigate = useNavigate();
  const { societaId, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const canEdit = can(MOD, 'edit');
  const canDelete = can(MOD, 'delete');
  const [modelli, setModelli] = useState<ModelloElenco[] | null>(null);
  const [errore, setErrore] = useState('');
  const [inModifica, setInModifica] = useState<ModelloElenco | 'nuovo' | null>(null);
  const [daEliminare, setDaEliminare] = useState<ModelloElenco | null>(null);
  const [guidaAperta, setGuidaAperta] = useState(false);
  const richiestaCorrente = useRef(0);

  const carica = useCallback(() => {
    const richiesta = ++richiestaCorrente.current;
    setErrore('');
    if (!pronta || !societaId) {
      setModelli(null);
      return;
    }
    // @api GET /api/percorso-acquirenti/modelli?societaId -> {"success":true,"data":[{"id":"number","nome":"string","descrizione":"string","updatedAt":"string","numeroCantieri":"number","risorse":"array"}]}
    apiFetch<{ success: boolean; data: ModelloElenco[] }>(`/api/percorso-acquirenti/modelli?societaId=${societaId}`)
      .then(r => { if (richiesta === richiestaCorrente.current) setModelli(r.data); })
      .catch(e => { if (richiesta === richiestaCorrente.current) setErrore(e.message); });
  }, [pronta, societaId]);

  // Cambiando società si svuota l'elenco: non devono restare modelli (né azioni) dell'altra società.
  useEffect(() => {
    setModelli(null);
    setInModifica(null);
    setDaEliminare(null);
  }, [societaId]);
  useEffect(() => { carica(); }, [carica]);

  const scarica = (m: ModelloElenco, formato: 'html' | 'css') => {
    // @api GET /api/percorso-acquirenti/modelli/:id/file/html -> file HTML (attachment)
    // @api GET /api/percorso-acquirenti/modelli/:id/file/css -> file CSS (attachment)
    apiDownload(`/api/percorso-acquirenti/modelli/${m.id}/file/${formato}`, `${nomeFileSicuro(m.nome)}.${formato}`)
      .catch(e => showToast(e.message, 'error'));
  };

  const scaricaEsempio = async () => {
    try {
      // @api GET /api/percorso-acquirenti/modelli/esempio/html -> file HTML (attachment)
      await apiDownload('/api/percorso-acquirenti/modelli/esempio/html', 'modello-esempio.html');
      // @api GET /api/percorso-acquirenti/modelli/esempio/css -> file CSS (attachment)
      await apiDownload('/api/percorso-acquirenti/modelli/esempio/css', 'modello-esempio.css');
      showToast('Scaricati modello-esempio.html e modello-esempio.css');
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const vaiAnteprima = (m: ModelloElenco) => navigate(`/commerciale/percorso/modelli/${m.id}/anteprima`);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Modelli pagina</h1>
          <p className="bop-page-subtitle">Modelli grafici della pagina di prenotazione, da scegliere cantiere per cantiere</p>
        </div>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" onClick={() => setGuidaAperta(true)}>Guida ai segnaposto</button>
          <button className="bop-btn bop-btn-secondary" onClick={scaricaEsempio}>Scarica modello di esempio</button>
          {canEdit && <button className="bop-btn bop-btn-primary" onClick={() => setInModifica('nuovo')} disabled={!societaId}>Nuovo modello</button>}
        </div>
      </div>

      {!societaId && <div className="bop-empty-state">Seleziona una società in alto</div>}
      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      {societaId && (
        <div className="bop-card">
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Nome</th><th>Immagini</th><th>Cantieri</th><th>Aggiornato</th><th className="bop-table__th--actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {!modelli || modelli.length === 0 ? (
                // Con un errore e nessun dato "Nessun modello" sarebbe falso: basta l'avviso sopra.
                (modelli || !errore) && <tr><td colSpan={5}><div className="bop-empty-state">{modelli ? 'Nessun modello: scarica il modello di esempio e crea il primo' : 'Caricamento…'}</div></td></tr>
              ) : modelli.map(m => (
                <tr key={m.id} onClick={() => (canEdit ? setInModifica(m) : vaiAnteprima(m))}>
                  <td>
                    <strong>{m.nome}</strong>
                    {m.descrizione && <><br /><span className="bop-text-muted">{m.descrizione}</span></>}
                  </td>
                  <td>{m.risorse.length}</td>
                  <td>{m.numeroCantieri}</td>
                  <td>{formatDataRoma(m.updatedAt)}</td>
                  <td className="bop-table__td--actions">
                    <BopRowActions actions={[
                      { icon: <IconaAnteprima />, label: 'Anteprima', onClick: () => vaiAnteprima(m), variant: 'primary' },
                      { icon: <RowIcon.Download />, label: 'Scarica HTML', onClick: () => scarica(m, 'html') },
                      { icon: <RowIcon.Download />, label: 'Scarica CSS', onClick: () => scarica(m, 'css') },
                      { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => setDaEliminare(m), variant: 'danger', hidden: !canDelete },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inModifica && societaId && (
        <ModaleModello
          key={inModifica === 'nuovo' ? 'nuovo' : inModifica.id}
          societaId={societaId}
          modello={inModifica === 'nuovo' ? null : inModifica}
          onClose={() => setInModifica(null)}
          onSalvato={() => { setInModifica(null); carica(); }}
        />
      )}
      {daEliminare && (
        <ModaleElimina key={daEliminare.id} modello={daEliminare} onClose={() => setDaEliminare(null)} onEliminato={() => { setDaEliminare(null); carica(); }} />
      )}
      {guidaAperta && <ModaleGuida onClose={() => setGuidaAperta(false)} />}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
